import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import type { TagVariant } from '@sunwebgroup/uds-ui/tag'
import type { FlightLeg } from '../../../domain/types'
import { AirlineIcon } from '@sunwebgroup/uds-ui/airline-icon'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Tooltip } from '@sunwebgroup/uds-ui/tooltip'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { formatDate } from '@/scopes/account/common/formatters'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import {
  formatFlightTime,
  getAirlineIconGlyph,
} from '../../utils/flightDetails'
import { FlightDetailsCardProps } from './FlightDetailsCard'
import styles from './FlightLegDetails.module.css'

interface FlightTypeTagConfig {
  translationKey: string
  variant: TagVariant
  icon: IconGlyph
}

const FLIGHT_TYPE_TAG_CONFIG: Record<'flexible' | 'standard', FlightTypeTagConfig> = {
  flexible: {
    translationKey: 'booking_details_flight_flexible',
    variant: 'success-tinted',
    icon: 'AirplaneWithArrowsCirclePath',
  },
  standard: {
    translationKey: 'booking_details_flight_standard',
    variant: 'neutral-tinted',
    icon: 'AirplaneToTop',
  },
}

interface FlightLegDetailsProps {
  leg: FlightLeg
  direction: FlightDetailsCardProps['direction']
  isCancelled: boolean
}

export function FlightLegDetails({ leg, direction, isCancelled }: FlightLegDetailsProps) {
  const { formatMessage } = useIntl()
  const locale = useConfiguration('locale')
  const airlineIconGlyph = getAirlineIconGlyph(leg.airlineCode)
  const directionIcon = direction === 'outbound' ? 'AirplaneTakingOff' : 'AirplaneReturn'
  const directionLabel = formatMessage({
    id: direction === 'outbound' ? 'booking_details_flight_outbound' : 'booking_details_flight_inbound',
  })
  const flightTypeTag = FLIGHT_TYPE_TAG_CONFIG[leg.isFlexibleFlight ? 'flexible' : 'standard']

  return (
    <div className={styles.legBlock}>
      <div className={styles.flightInfoMain}>
        <div className={styles.timesColumn}>
          <div className={styles.timeBlock}>
            <Typography variant="body-l-bold-tight">
              {formatFlightTime(leg.departureDateTime, locale, leg.showDepartureAndArrivalTimes)}
            </Typography>
            <Typography variant="body-s-regular-tight" className={styles.timeDate}>
              {formatDate(leg.departureDateTime, locale)}
            </Typography>
          </div>
          <div className={styles.timeBlock}>
            <Typography variant="body-l-bold-tight">
              {formatFlightTime(leg.arrivalDateTime, locale, leg.showDepartureAndArrivalTimes)}
            </Typography>
            <Typography variant="body-s-regular-tight" className={styles.timeDate}>
              {formatDate(leg.arrivalDateTime, locale)}
            </Typography>
          </div>
        </div>

        <div className={styles.separator} role="group" aria-label={directionLabel}>
          <div className={styles.separatorTop}>
            <span className={styles.separatorSpacer} aria-hidden="true" />
            <span className={styles.separatorDot} aria-hidden="true" />
            <span className={styles.separatorLine} aria-hidden="true" />
          </div>
          <div className={styles.separatorMiddle}>
            <Icon glyph={directionIcon} size={20} className={styles.separatorIcon} aria-hidden="true" />
          </div>
          <div className={styles.separatorBottom}>
            <span className={styles.separatorLine} aria-hidden="true" />
            <span className={styles.separatorDot} aria-hidden="true" />
            <span className={styles.separatorSpacer} aria-hidden="true" />
          </div>
        </div>

        <div className={styles.locationsColumn}>
          <div className={styles.locationBlock}>
            <Typography variant="body-l-bold-tight">
              {leg.departureAirportName}
            </Typography>
            <Typography variant="body-s-regular-tight" className={styles.secondaryText}>
              {leg.departureAirportCode}
            </Typography>
          </div>
          <div className={styles.airlineInfo}>
            {airlineIconGlyph && <AirlineIcon glyph={airlineIconGlyph} size="sm" />}
            <Typography variant="body-s-regular-flat">
              {leg.airlineName ? `${leg.airlineName} - ${leg.flightNumber}` : leg.flightNumber}
            </Typography>
          </div>
          <div className={styles.locationBlock}>
            <Typography variant="body-l-bold-tight">
              {leg.arrivalAirportName}
            </Typography>
            <Typography variant="body-s-regular-tight" className={styles.secondaryText}>
              {leg.arrivalAirportCode}
            </Typography>
          </div>
        </div>
      </div>

      <div className={styles.flightInfoFooter}>
        <div className={styles.footerTags}>
          {leg.passengerNameRecord && !isCancelled && (
            <Tooltip.Root>
              <Tooltip.Trigger>
                <Tag variant="neutral-tinted" size="M">
                  {`PNR: ${leg.passengerNameRecord}`}
                </Tag>
              </Tooltip.Trigger>
              <Tooltip.Content
                text={formatMessage({ id: 'booking_details_flight_pnr_description' })}
                position="bottom-start"
              />
            </Tooltip.Root>
          )}
          <Tag variant={flightTypeTag.variant} size="M" icon={flightTypeTag.icon} className={styles.flightTypeTag}>
            {formatMessage({ id: flightTypeTag.translationKey })}
          </Tag>
        </div>
        {leg.remarks && (
          <div className={styles.remarksArea}>
            <Banner
              color="informative"
              size="S"
              hasIcon
              contentIsCentered
            >
              <Banner.TextsWrapper>
                <Banner.Text>
                  {formatMessage({ id: 'booking_details_flight_remark' }, { remark: leg.remarks })}
                </Banner.Text>
              </Banner.TextsWrapper>
            </Banner>
          </div>
        )}
      </div>
    </div>
  )
}
